import { useContext, useEffect, useMemo, useState } from 'react'
import styles from './diagnosis-page.module.scss'
import { Button } from 'antd'
import { STUDENT, PLATFORM_TALLY } from '@BUP/constants'
import { DownloadOutlined } from '@ant-design/icons'
import PageLayout from '@BUP/components/Layout/Page'
import CircleProgressCard from '@BUP/components/Data/CircleProgressCard'
import PercentageProgressCard from '@BUP/components/Data/PercentageProgressCard'
import ModalComponent from '@BUP/components/Generics/Modal'
import GetReportComponent from '@BUP/components/Data/getReportComponent'
import { DiagnosisContext } from '@BUP/contexts/DiagnosisContext'
import { useGetCurrent } from '@BUP/hooks/Current/useGetCurrent'
import { useGetDiagnosisProgress } from '@BUP/hooks/Data/useGetDiagnosisProgress'
import { useGetReport } from '@BUP/hooks/Data/useGetReport'
import { useTranslation } from 'react-i18next'
import RoleStatusCardComponents from '@BUP/components/Data/roleStatusCardComponents/role-status-card-components'
import HeaderInfoSurveyProgressComponent from '@BUP/components/Data/headerInfoSurveyProgressComponent/header-info-survey-progress-component'
import {
  buildLevelMapping,
  sanitizeSelectedLevel
} from '@BUP/utils/diagnosticLevels'
import {
  buildTallyLevels,
  buildTallyProgressMapping,
  buildTallySettingsMapping
} from '@BUP/utils/tallyDiagnostic'

const DiagnosticPage = () => {
  const i18n = useTranslation()
  const {
    currentData: roles
  } = useGetCurrent('roles', 3)
  const [showModal, setShowModal] = useState(false)
  const [selectedLevel, setSelectedLevel] = useState(null)
  const diagnosisSurveys = useContext(DiagnosisContext)
  const { getDiagnosisProgress, surveyProgress, loading, isRefetching } =
  useGetDiagnosisProgress(diagnosisSurveys)
  const { getReport, report, error, loading: loadingReport } = useGetReport()
  const studentRole = roles && roles.find(role => role.type === STUDENT)
  const diagnosticSurveyId =
    diagnosisSurveys &&
    diagnosisSurveys?.find(survey => survey?.roleId === studentRole?.id)?.id

  const handleGetReport = () => {
    setShowModal(true)
    if (diagnosticSurveyId) {
      getReport(diagnosticSurveyId)
    }
  }

  // Tally surveys are deduped before they reach here (useGetDiagnosisSurveys),
  // so the list already holds one Tally config per (roleId, tallyFormId).
  // Normalize each Tally entry into the LimeSurvey-compatible shape the
  // original cards consume: settings → { total, min, enivel } and progress →
  // { total, enivel }. LimeSurvey entries pass through unchanged.
  const normalizedDiagnosisSurveys = useMemo(() => {
    return (diagnosisSurveys || []).map((survey) =>
      survey?.platform === PLATFORM_TALLY
        ? { ...survey, settings: buildTallySettingsMapping(survey) }
        : survey
    )
  }, [diagnosisSurveys])

  const normalizedSurveyProgress = useMemo(() => {
    if (!Array.isArray(surveyProgress)) return []
    return surveyProgress.map((survey) =>
      survey?.platform === PLATFORM_TALLY
        ? { ...survey, ...buildTallyProgressMapping(survey) }
        : survey
    )
  }, [surveyProgress])

  const surveyCompletedArray = normalizedSurveyProgress.map((survey) => survey.total)
  const surveyMinArray = normalizedDiagnosisSurveys.map((survey) => survey.settings.min)

  const totalSum = surveyCompletedArray.reduce((acc, curr) => acc + (Number(curr) || 0), 0)
  const totalSumMin = surveyMinArray.reduce((acc, curr) => acc + (Number(curr) || 0), 0)

  const totalProgress = totalSumMin ? Math.round((totalSum / totalSumMin) * 100) : 0

  const calculateProgressByRole = roleType => {
    const roleId = roles && roles.find(role => role.type === roleType)?.id
    const isRoleParticpating = !!(
      normalizedDiagnosisSurveys &&
      normalizedDiagnosisSurveys.find(survey => survey.roleId === roleId)
    )
    if (!isRoleParticpating) {
      return {
        surveyTotalData: 0,
        totalAnswers: 0
      }
    }
    const surveyTotalData =
      normalizedDiagnosisSurveys &&
      normalizedDiagnosisSurveys.find(survey => survey.roleId === roleId)
    const totalAnswers =
      normalizedSurveyProgress &&
      normalizedSurveyProgress?.find(survey => survey.roleId === roleId)

    return { surveyTotalData, totalAnswers }
  }

  const studentData = calculateProgressByRole(STUDENT)

  const levelOptions = useMemo(() => {
    const settings = studentData?.surveyTotalData?.settings
    if (studentData?.surveyTotalData?.platform === PLATFORM_TALLY) {
      return buildTallyLevels(studentData.surveyTotalData, studentData.totalAnswers)
    }
    return buildLevelMapping(settings, i18n.i18n.language)
  }, [studentData?.surveyTotalData, studentData?.totalAnswers, i18n.i18n.language])

  useEffect(() => {
    const sanitizedSelection = sanitizeSelectedLevel(selectedLevel, levelOptions)
    if (sanitizedSelection !== selectedLevel) {
      setSelectedLevel(sanitizedSelection)
    }
  }, [selectedLevel, levelOptions])

  return (
    <PageLayout
      pageName={i18n.t('pages.diagnostic.title')}
      backButton
      textColor='#17bebb'
    >
      <div className={styles.container}>
        <div className={styles.text_wrapper}>
          <HeaderInfoSurveyProgressComponent
            buttonText={i18n.t('pages.diagnostic.download-report')}
            handleGetReport={handleGetReport}
            actionText={i18n.t('pages.diagnostic.important-reminder')}
            icon={<DownloadOutlined className={styles.download_icon} />}
          />
        </div>
        <div className={styles.report_wrapper}>
          <Button
            type='primary'
            shape='round'
            onClick={getDiagnosisProgress}
          >
            {i18n.t('pages.diagnostic.reload-answers')}
          </Button>
        </div>
        <div className={styles.progress_info_wrapper}>
          <CircleProgressCard percent={totalProgress || 0} title={i18n.t('pages.diagnostic.total-answers')} />
          <RoleStatusCardComponents loading={loading || isRefetching} studentData={studentData} calculateProgressByRole={calculateProgressByRole} />
        </div>
        <PercentageProgressCard
          studentData={studentData}
          availableLevels={levelOptions}
          selectedLevel={selectedLevel}
        />
        <ModalComponent
          header
          headerText={i18n.t('pages.diagnostic.report.title')}
          stateModal={showModal}
          showButtons={false}
          onClickCloseModal={() => setShowModal(false)}
          modalDescription={
            <GetReportComponent
              report={report}
              loading={loadingReport}
              error={error}
            />
          }
        />
      </div>
    </PageLayout>
  )
}

export default DiagnosticPage
